import { useMemo } from 'react'
import useDataElements from '@/hooks/useDataElements'
import {
  V1_DATA_ELEMENT_ID,
  V1_TAB_LABEL,
  columnsFromConfig,
  filtersFromConfig,
  hasListingConfig,
  labelFor,
} from '@/pages/Submittals/listing-config'

/**
 * One tab per data element the consultant can review.
 *
 * Unlike pitstop-ui's version this applies no produce/consume gating. There, a
 * tab is shown to an org that produces the data element, or to a named secondary
 * approver of one it is an allowed receiver for. Here the caller is a consultant
 * authenticated by OTP: they neither produce nor receive on an org's behalf, and
 * `GET /consultant/submittals` already restricts every row to the submittals
 * assigned to their email. Re-deriving that in the client would only be able to
 * hide tabs the API would happily serve.
 *
 * `environmental_monthly_submissions` carries no `listingConfig` — its table is
 * the hardcoded one that predates the config — so it gets a tab of its own
 * rather than being derived like the rest.
 */
const useListingTabs = () => {
  const { dataElements, isLoading, error } = useDataElements()

  const tabs = useMemo(() => {
    const records = Array.isArray(dataElements) ? dataElements : []
    const v1Record = records.find(de => de.dataElementId === V1_DATA_ELEMENT_ID)

    const v1Tab = {
      dataElementId: V1_DATA_ELEMENT_ID,
      label: (v1Record && labelFor(v1Record)) || V1_TAB_LABEL,
      // Read like a config tab's: tells the listing whether second-stage
      // statuses are reachable.
      secondaryApproval: {
        required: v1Record?.requiresSecondaryApproval === true,
        label: v1Record?.secondaryApprovalLabel,
      },
      isV1: true,
    }

    const configTabs = records
      .filter(de => de.dataElementId !== V1_DATA_ELEMENT_ID && hasListingConfig(de))
      .map(de => ({
        dataElementId: de.dataElementId,
        label: labelFor(de),
        columns: columnsFromConfig(de),
        filters: filtersFromConfig(de),
        // Workflow config, read by name rather than by scanning the root.
        // Stored as two attributes, consumed as one concept.
        secondaryApproval: {
          required: de.requiresSecondaryApproval === true,
          label: de.secondaryApprovalLabel,
        },
        isV1: false,
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)))

    return [v1Tab, ...configTabs]
  }, [dataElements])

  // `dataElements` is null until the fetch settles, so "no config tabs" is only
  // meaningful once this is true. Derived from `isLoading` alone rather than
  // from `dataElements !== null`: a failed load leaves it null, and treating
  // that as "not settled yet" would hold the page on a loader forever. The
  // provider clears `isLoading` on every path, success or not.
  const isLoaded = !isLoading

  return { tabs, isLoaded, hasConfigTabs: tabs.some(tab => !tab.isV1), error }
}

export default useListingTabs
